import {
  IncentivePlan as ApiIncentivePlan,
  IncentivePlanReadOnly,
  IncentivePlansResponse,
} from 'lib/generated/api'
import { IncentivePlan } from 'types/entities'
import { microAmountToFixed } from 'utils/conversion'
import {
  computeCompanies,
  computeMerchantList,
  computePeople,
  computeSchedule,
  computeStatus,
  merchantEligibilityTypeMap,
  merchantRadiusUnitMap,
  parseIncentiveType,
} from './helpers'

export type IncentivePlanMapper = (
  data: ApiIncentivePlan,
  defaultTimezone: string
) => IncentivePlan
export const mapToIncentivePlan: IncentivePlanMapper = (
  data,
  defaultTimezone
) => {
  const {
    metadata,
    draftConfiguration: draft,
    launchedConfiguration: launched,
  } = data
  const meta = metadata as IncentivePlanReadOnly
  const name = draft?.name || launched?.name
  const description = draft?.description || launched?.description
  const incentiveTemplate =
    draft?.incentiveTemplate || launched?.incentiveTemplate
  const merchantEligibility =
    draft?.merchantEligibility || launched?.merchantEligibility
  const userTargeting = draft?.userTargeting || launched?.userTargeting

  const schedule = computeSchedule(
    defaultTimezone,
    draft?.schedule || launched?.schedule,
    incentiveTemplate?.availableFrom,
    incentiveTemplate?.availableTo,
    incentiveTemplate?.duration
  )
  const incentivePlan: IncentivePlan = {
    id: meta.id,
    status: computeStatus(meta.status, meta.scheduleStatus),
    hasChangesDrafted: !!draft,
    canLaunch: !!meta.actions.publish && meta.status === 'DRAFT',
    canRelaunch: !!meta.actions.publish && meta.status === 'LAUNCHED',
    canPause: !!meta.actions.pause,
    canResume: !!meta.actions.resume,
    canDelete: !!meta.actions._delete,
    incentiveDetails: {
      name,
      description,
      type: parseIncentiveType(incentiveTemplate?.type, schedule?.type),
      currencyCode: incentiveTemplate?.currencyCode,
      creditAmount: microAmountToFixed(incentiveTemplate?.creditAmount),
      discountAmount: microAmountToFixed(incentiveTemplate?.discountAmount),
      discountPercentage: incentiveTemplate?.discountPercentage,
      discountValueMax: microAmountToFixed(incentiveTemplate?.discountValueMax),
      orderValueMin: microAmountToFixed(incentiveTemplate?.orderValueMin),
      schedule,
    },
    merchantParticipation: {
      original: merchantEligibility,
      type: merchantEligibility?.type
        ? merchantEligibilityTypeMap[merchantEligibility.type]
        : 'all_merchants',
      merchantList: computeMerchantList(merchantEligibility?.merchantListGroup),
      merchantRadius: merchantEligibility?.merchantRadius,
      merchantRadiusUnit: merchantEligibility?.merchantRadiusUnit
        ? merchantRadiusUnitMap[merchantEligibility.merchantRadiusUnit]
        : undefined,
    },
    recipients: {
      original: userTargeting,
      allCompanies: userTargeting?.worspaceGroupListType
        ? userTargeting.worspaceGroupListType === 'ALL'
        : false,
      companies: computeCompanies(userTargeting?.workspaceGroup),
      allPeople: userTargeting?.userGroupListType
        ? userTargeting.userGroupListType === 'ALL'
        : false,
      people: computePeople(userTargeting?.userGroup),
    },
  }
  return incentivePlan
}

export type IncentivePlansMapper = (
  data: IncentivePlansResponse,
  defaultTimezone: string
) => Omit<IncentivePlansResponse, 'results'> & { results: IncentivePlan[] }
export const mapToIncentivePlans: IncentivePlansMapper = (
  data,
  defaultTimezone
) => {
  const { results, ...rest } = data
  return {
    results: results.map(incentivePlan =>
      mapToIncentivePlan(incentivePlan, defaultTimezone)
    ),
    ...rest,
  }
}
